import React, { ReactElement } from 'react';

import { Route, Navigate } from 'react-router-dom';
import { ApplicationModule } from '../shared/applicationModule';
import RolesSettingsPage from './roles/RolesSettingsPage';
import SettingsPagesWrapper from './SettingsPagesWrapper';
import UpdateProfilePage from './profile/UpdateProfilePage';
import PasswordAndLoginMethodsPage from './password/PasswordAndLoginMethodsPage';
import BillingPage from './billing/BillingPage';
import { Breadcrumb, BreadcrumbsWrapper } from '../shared/web/layout/Breadcrumbs';
import ProjectDetailsFieldsListPage from './projectdetails/List/ProjectDetailsFieldsList';
import { ClientPortalBrandingPage } from './clientportal/ClientPortalBrandingPage';
import ServiceProvidersSettingsPage from './serviceproviders/ServiceProvidersSettingsPage';
import ImpersonateUsersPage from './administration/impersonateusers/ImpersonateUsersPage';
import DocumentLabelsPage from './documentstorage/labels/DocumentLabelsPage';
import DocumentsTemplatesPage from './documentstorage/templates/DocumentsTemplatesPage';
import AiTrainerPage from './administration/aitrainer/AiTrainerPage';
import CreateTrainingEntryPage from './administration/aitrainer/Create/CreateTrainingEntryPage';
import UpdateTrainingEntryPage from './administration/aitrainer/Update/UpdateTrainingEntryPage';
import PortalConfigurationPage from './clientportal/PortalConfigurationPage';
import NotificationsSettingsPage from './notifications/NotificationsSettingsPage';
import PreferencesPage from './preferences/PreferencesPage';
import TeamMembersPage from './teammembers/TeamMembersPage';
import UpdateTeamMemberPage from './teammembers/UpdateTeamMemberPage';
import TeamMemberHistoryPage from './teammembers/history/TeamMemberHistoryPage';
import TasksGroupsList from './tasksgroups/TasksGroupsList';
import DetailsFieldsSetPage from './projectdetails/Sets/List/DetailsFieldsSetPage';
import DetailsFieldsPageWrapper from './projectdetails/shared/DetailsFieldsPageWrapper';
import EmailConfigurationPage from './emaildomain/EmailConfigurationPage';
import SendingEmailPage from './sendingemail/SendingEmailPage';
import BusinessDaysConfigurationPage from './businessdays/BusinessDaysConfigurationPage';
import CalendarIntegrationPage from './calendarintegration/CalendarIntegrationPage';
import CalendarIntegrationCallbackPage from './calendarintegration/CalendarIntegrationCallbackPage';
import WebhookSubscriptionsPage from './integrations/webhooks/WebhookSubscriptionsPage';
import WebhookRequestsLogPage from './integrations/webhooks/monitoring/WebhookRequestsLogPage';
import ApiTokensPage from './apitokens/ApiTokensPage';
import FubOauthCode from './integrations/fub/FubOauthCode';
import FubConnectionProfilePage from './integrations/fub/FubConnectionProfilePage';
import SetupAreasAdminPage from './administration/markets/SetupAreasAdminPage';
import DetailsFieldGroupsPage from './administration/detailsgroups/DetailsFieldGroupsPage';
import { DetailFieldsListPage } from './administration/markets/detailboxes/DetailFieldsListPage';
import PlansList from './administration/markets/plans/PlansList';
import SetupAreaVersionWrapper from './administration/markets/SetupAreaVersionWrapper';
import { AiTrainingPage } from './administration/markets/aitraining';
import MarketsPage from './markets/MarketsPage';
import MarketDetailBoxesPage from './markets/MarketDetailBoxesPage';
import { TasksGroupsAdminList } from './administration/tasksgroups';
import { NewPortalConfigurationPage } from './clientportal/NewPortalConfigurationPage';
import SetupAreaVersionPage from './administration/markets/SetupAreaVersionPage';
import MarketRequestsListPage from './markets/requests/MarketRequestsListPage';
import MarketsRequestsPage from './administration/markets/requests/MarketsRequestsPage';
import { MarketRequestPage } from './administration/markets/requests/MarketRequestPage';
import SetupAreaPage from './administration/markets/versions/SetupAreaPage';
import { CustomProcessDetailFieldsPage } from './markets/customprocesses';
import OAuthCallbackPage from './password/oauth/OAuthCallbackPage';
import UnifiedEmailPage from "./email/UnifiedEmailPage";
import EmailOAuthCallbackPage from './email/oauth/EmailOAuthCallbackPage';

export function SettingsBreadcrumbs(
  props: {
    children?: ReactElement,
  }
) {
  return (
    <BreadcrumbsWrapper>
      <Breadcrumb text="Settings" href="/settings/markets">
        {props.children}
      </Breadcrumb>
    </BreadcrumbsWrapper>
  );
}

export default function useSettingsModule(): ApplicationModule {
  const getRouter = () => {
    return (
      <>
        <Route path="/settings" element={<SettingsPagesWrapper title="Settings" />}>
          <Route
            path="/settings/roles"
            element={<RolesSettingsPage />}
          />
          <Route
            path="/settings/profile"
            element={<UpdateProfilePage />}
          />
          <Route
            path="/settings/sendingemail"
            element={<SendingEmailPage />}
          />
          <Route
            path="/settings/email"
            element={<UnifiedEmailPage />}
          />
          <Route
            path="/settings/email/gmail"
            element={<Navigate to="/settings/email" replace />}
          />
          <Route
            path="/settings/email/outlook"
            element={<Navigate to="/settings/email" replace />}
          />
          <Route
            path="/settings/email/:type"
            element={<UnifiedEmailPage />}
          />
          <Route
            path="/settings/password"
            element={<PasswordAndLoginMethodsPage />}
          />
          <Route
            path="/settings/password/oauth/:provider/callback"
            element={<OAuthCallbackPage />}
          />
          <Route
            path="/settings/email/oauth/:provider/callback"
            element={<EmailOAuthCallbackPage />}
          />
          <Route
            path="/settings/billing"
            element={<BillingPage />}
          />
          <Route
            path="/settings/detailboxes/:projectType"
            element={<DetailsFieldsPageWrapper />}
          >
            <Route path="" element={<ProjectDetailsFieldsListPage />} />
            <Route path="/settings/detailboxes/:projectType/:setId"
                   element={<DetailsFieldsSetPage />} />
          </Route>
          <Route path="/settings/clientportal" element={<ClientPortalBrandingPage />} />
          <Route path="/settings/clientportal/new" element={<NewPortalConfigurationPage />} />
          <Route path="/settings/clientportal/new/:setupAreaId/:projectType"
                 element={<NewPortalConfigurationPage />} />
          <Route path="/settings/clientportal/:projectType/default"
                 element={<PortalConfigurationPage />} />
          <Route path="/settings/emailconfiguration" element={<EmailConfigurationPage />} />
          <Route path="/settings/serviceproviders" element={<ServiceProvidersSettingsPage />} />
          <Route path="/settings/documentlabels" element={<DocumentLabelsPage />} />
          <Route path="/settings/documentstemplates" element={<DocumentsTemplatesPage />} />
          <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
          <Route path="/settings/preferences" element={<PreferencesPage />} />
          <Route path="/settings/members" element={<TeamMembersPage />} />
          <Route path="/settings/calendarintegration" element={<CalendarIntegrationPage />} />
          <Route path="/settings/calendarintegration/callback"
                 element={<CalendarIntegrationCallbackPage />} />
          <Route path="/settings/tasksgroups" element={<TasksGroupsList />} />
          <Route path="/settings/holidays" element={<BusinessDaysConfigurationPage />} />

          <Route path="/settings/detailsgroups" element={<DetailsFieldGroupsPage />} />
          <Route path="/settings/webhooks" element={<WebhookSubscriptionsPage />} />
          <Route path="/settings/webhooks/:id" element={<WebhookRequestsLogPage />} />
          <Route
            path="/settings/apitokens"
            element={<ApiTokensPage />}
          />

          <Route path="/settings/integrations/fub/code" element={<FubOauthCode />} />
          <Route path="/settings/integrations/fub" element={<FubConnectionProfilePage />} />
          <Route path="/settings/markets" element={<MarketsPage />} />
          <Route path="/settings/markets/requests" element={<MarketRequestsListPage />} />
          <Route path="/settings/markets/:setupAreaId/detailboxes"
                 element={<MarketDetailBoxesPage />} />
          <Route path="/settings/markets/:setupAreaId/detailboxes/:projectType"
                 element={<MarketDetailBoxesPage />} />
          <Route
            path="/settings/customprocesses/:setupAreaId"
            element={<CustomProcessDetailFieldsPage />}
          />
        </Route>
        <Route path="/settings/members/:id" element={<UpdateTeamMemberPage />} />
        <Route path="/settings/members/:id/history" element={<TeamMemberHistoryPage />} />
        <Route path="/admin"
               element={<SettingsPagesWrapper title="Admin Panel" hideSettingsMenu />}>
          <Route path="/admin/aitrainer" element={<AiTrainerPage />} />
          <Route path="/admin/aitrainer/create" element={<CreateTrainingEntryPage />} />
          <Route path="/admin/aitrainer/entries/:id" element={<UpdateTrainingEntryPage />} />
          <Route path="/admin/users" element={<ImpersonateUsersPage />} />
          <Route path="/admin/setupareas" element={<SetupAreasAdminPage />} />
          <Route path="/admin/setupareas/:state" element={<SetupAreasAdminPage />} />
          <Route path="/admin/tasksgroups" element={<TasksGroupsAdminList />} />
          <Route path="/admin/marketsrequests" element={<MarketsRequestsPage />} />
          <Route path="/admin/marketsrequests/:id" element={<MarketRequestPage />} />
          <Route
            path="/admin/setupareas/:state/:setupAreaId"
            element={<SetupAreaPage />}
          />
          <Route path="/admin/setupareas/:state/:setupAreaId/:setupAreaVersion"
                 element={<SetupAreaVersionWrapper />}>
            <Route path="" element={<SetupAreaVersionPage />} />
            <Route path="/admin/setupareas/:state/:setupAreaId/:setupAreaVersion/workflows"
                   element={<PlansList />} />
            <Route path="/admin/setupareas/:state/:setupAreaId/:setupAreaVersion/workflows/:id"
                   element={<PlansList />} />
            <Route path="/admin/setupareas/:state/:setupAreaId/:setupAreaVersion/detailboxes"
                   element={(<DetailFieldsListPage />)} />
            <Route path="/admin/setupareas/:state/:setupAreaId/:setupAreaVersion/aitraining"
                   element={(<AiTrainingPage />)} />
          </Route>
        </Route>
      </>
    );
  };

  return {
    getRouter,
    name: 'settings'
  };
}
